import React, { useState } from 'react';

import { ChevronRight, Cpu, Loader2 } from 'lucide-react';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import type { ModelUsageItem } from '../../../lib/api/credits';
import { cn } from '../../../lib/utils';

/** Friendly display names for the current model lineup.
 * Includes both short aliases and backend-resolved IDs (see app-config.service.ts).
 */
const MODEL_LABELS: Record<string, string> = {
  // Claude
  'claude-4-opus': 'Claude Opus 5.5',
  'claude-opus-5-5': 'Claude Opus 5.5',
  'claude-opus-4-7': 'Claude 4.7 Opus', // legacy — historical usage rows
  'claude-4-sonnet': 'Claude Sonnet 5.5',
  'claude-sonnet-5-5': 'Claude Sonnet 5.5',
  'claude-sonnet-5': 'Claude Sonnet 5', // legacy — historical usage rows
  'claude-sonnet-4-6': 'Claude 4.6 Sonnet', // legacy — historical usage rows

  'claude-4-haiku': 'Claude 4.5 Haiku',
  'claude-haiku-4-5': 'Claude 4.5 Haiku',
  'claude-haiku-4-5-20251001': 'Claude 4.5 Haiku',
  // OpenRouter
  'openai/gpt-5.4': 'GPT-5.4', // legacy — historical usage rows (replaced by 5.6 Terra)
  'openrouter/openai/gpt-5.4': 'GPT-5.4', // legacy — historical usage rows
  'openai/gpt-5.6-terra': 'GPT-5.6 Terra',
  'openrouter/openai/gpt-5.6-terra': 'GPT-5.6 Terra',
  'google/gemini-3.5-flash': 'Gemini 3.5 Flash',
  'openrouter/google/gemini-3.5-flash': 'Gemini 3.5 Flash',
  'qwen/qwen3.5-plus-02-15': 'Qwen 3.5 Plus', // legacy — historical usage rows (replaced by 3.7 Plus)
  'openrouter/qwen/qwen3.5-plus-02-15': 'Qwen 3.5 Plus', // legacy — historical usage rows
  'qwen/qwen3.7-plus': 'Qwen 3.7 Plus', // legacy — historical usage rows (dropped: no ZDR provider)
  'openrouter/qwen/qwen3.7-plus': 'Qwen 3.7 Plus', // legacy — historical usage rows
  'xiaomi/mimo-v2.5-pro': 'MiMo V2.5 Pro', // legacy — historical usage rows (replaced by V2.6 Pro)
  'openrouter/xiaomi/mimo-v2.5-pro': 'MiMo V2.5 Pro', // legacy — historical usage rows
  'xiaomi/mimo-v2.6-pro': 'MiMo V2.6 Pro',
  'openrouter/xiaomi/mimo-v2.6-pro': 'MiMo V2.6 Pro',
  // Codex engine (OpenAI models through the Codex harness, #90)
  'codex/gpt-6.1-sol': 'GPT-6.1 Sol (Codex)',
  'codex/gpt-6-luna': 'GPT-6 Luna (Codex)',
  'codex/gpt-6-astra': 'GPT-6 Astra (Codex)',
  'unknown': 'Unknown',
};

function getModelLabel(model: string): string {
  if (MODEL_LABELS[model]) return MODEL_LABELS[model];

  if (model.includes('/')) {
    const parts = model.split('/');
    const modelPart = parts[parts.length - 1];
    return modelPart
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  return model;
}

interface ModelUsageCardProps {
  usage: ModelUsageItem[] | null;
  loading: boolean;
}

export const ModelUsageCard: React.FC<ModelUsageCardProps> = ({ usage, loading }) => {
  const [open, setOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const totalCredits = usage?.reduce((sum, item) => sum + item.credits, 0) ?? 0;
  const totalCost = usage?.reduce((sum, item) => sum + item.costUsd, 0) ?? 0;
  const hasUsage = usage && usage.length > 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between py-3 px-1 hover:bg-muted/50 rounded-md transition-colors cursor-pointer">
        <div className="flex items-center gap-2">
          <ChevronRight
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform duration-200",
              open && "rotate-90"
            )}
          />
          <Cpu className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Usage by Model</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-mono text-muted-foreground">
            {totalCredits.toFixed(2)} credits
          </span>
          <span className="text-xs text-muted-foreground">
            ${totalCost.toFixed(4)}
          </span>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-6 mr-1 py-2 space-y-3">
          {!hasUsage ? (
            <p className="text-sm text-muted-foreground py-2 pl-2">
              No usage data yet for this billing cycle.
            </p>
          ) : (
            <>
              {usage.map((item) => {
                const pct = totalCredits > 0 ? (item.credits / totalCredits) * 100 : 0;

                return (
                  <div key={item.model} className="space-y-1.5 pl-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium truncate mr-4">{getModelLabel(item.model)}</span>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-xs text-muted-foreground">
                          {item.requests} req{item.requests !== 1 ? 's' : ''}
                        </span>
                        <span className="font-mono text-muted-foreground text-xs">
                          ${item.costUsd.toFixed(4)}
                        </span>
                        <span className="font-mono text-sm w-24 text-right">
                          {item.credits.toFixed(2)} cr
                        </span>
                      </div>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary/70 transition-all"
                        style={{ width: `${Math.max(pct, 1)}%` }}
                      />
                    </div>
                  </div>
                );
              })}

              <div className="pt-2 border-t border-border/50 flex justify-between items-center pl-2">
                <span className="text-xs font-medium text-muted-foreground">Total</span>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-muted-foreground">
                    ${totalCost.toFixed(4)}
                  </span>
                  <span className="font-mono text-sm font-medium w-24 text-right">
                    {totalCredits.toFixed(2)} cr
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
